/**
 * CTL (Close The Loop) Kusto Query Utilities
 * 
 * Provides query generation functions for the Explore Data buttons in CTL tabs.
 * These queries match the Kusto functions used by the backend to fetch data.
 */

// Kusto cluster and database configuration
const KUSTO_CLUSTER = "cxedataplatformcluster.westus2";
const KUSTO_DATABASE = "Security360";

/**
 * Base query for CTL Feature Evidences with SLA data
 * This is the main data source for the Overview tab
 */
export const CTL_BASE_QUERY = `// Close The Loop - Feature Evidences with SLA
cluster('${KUSTO_CLUSTER}').database('${KUSTO_DATABASE}').Fn_S360_CTL_getFeatureEvidencesWithSLA()`;

/**
 * Query for DCR Automation Details (Intake to Triage tab)
 */
export const CTL_DCR_AUTOMATION_QUERY = `// DCR Automation Details - Intake to Triage
cluster('${KUSTO_CLUSTER}').database('${KUSTO_DATABASE}').Fn_S360_getUnifiedInsightsDCRAutomationDetails()`;

/**
 * Query for Prioritize and Decide Details
 */
export const CTL_PRIORITIZE_DECIDE_QUERY = `// Prioritize and Decide Details
cluster('${KUSTO_CLUSTER}').database('${KUSTO_DATABASE}').Fn_S360_CTL_getPrioritizeAndDecideDetails()`;

/**
 * Query for Themes with Feature Evidences
 */
export const CTL_THEMES_QUERY = `// Themes with Feature Evidences
cluster('${KUSTO_CLUSTER}').database('${KUSTO_DATABASE}').Fn_S360_CTL_getThemesWithFeatureEvidences()`;

/**
 * Query for Feature Evidences (Track Requests tab)
 * This query returns all feature evidences without SLA data
 */
export const CTL_FEATURE_EVIDENCES_QUERY = `// Feature Evidences - Track Requests
cluster('${KUSTO_CLUSTER}').database('${KUSTO_DATABASE}').Fn_S360_CTL_getFeatureEvidences()`;

/**
 * Query for Customer Summary view
 */
export const CTL_CUSTOMER_SUMMARY_QUERY = `// Customer Summary - Track Requests
cluster('${KUSTO_CLUSTER}').database('${KUSTO_DATABASE}').Fn_S360_CTL_getFeatureEvidences()
| summarize 
    SubmissionCount = count(),
    OpenCount = countif(CustomerFacingStatus !has "Rejected" and CustomerFacingStatus !has "Available"),
    UniqueFeatures = dcount(FeatureRequestIdWithSource)
    by TPID, TPAccountName, TenantID, TenantName
| sort by SubmissionCount desc`;

/**
 * Query for DCR Requests view (Track Requests tab)
 * Mirrors the backend's GetCloseTheLoopDCRRequests Kusto query, which calls the
 * Fn_S360_CTL_getDCRRequests Security360 function. That function joins platform
 * identity (TPID / TenantId / TenantName / ProductName) and the Feature Request
 * association from UnifiedInsights onto the DCR dimension, so CRM identity is not
 * surfaced. All DCR records are returned regardless of FeatureRequestId association.
 */
export const CTL_DCR_REQUESTS_QUERY = `// DCR Requests - Track Requests
cluster('${KUSTO_CLUSTER}').database('${KUSTO_DATABASE}').Fn_S360_CTL_getDCRRequests()`;

/**
 * Generates a filtered query for Overview tab based on selected filters
 */
export const generateOverviewQuery = (options?: {
  products?: string[];
  dateRange?: "2years" | "all" | "custom";
  startDate?: string;
  endDate?: string;
}): string => {
  let query = CTL_BASE_QUERY;
  const filters: string[] = [];

  if (options?.products && options.products.length > 0) {
    const productList = options.products.map(p => `"${p}"`).join(", ");
    filters.push(`ProductName in (${productList})`);
  }

  if (options?.dateRange === "2years") {
    filters.push(`FeatureEvidenceCreatedDate >= ago(730d)`);
  } else if (options?.dateRange === "custom" && options.startDate && options.endDate) {
    filters.push(`FeatureEvidenceCreatedDate >= datetime(${options.startDate}) and FeatureEvidenceCreatedDate <= datetime(${options.endDate})`);
  }

  if (filters.length > 0) {
    query += `\n| where ${filters.join(" and ")}`;
  }

  return query;
};

/**
 * Generates query for Pipeline Snapshot breakdown
 */
export const generatePipelineSnapshotQuery = (options?: {
  products?: string[];
  dateRange?: "2years" | "all" | "custom";
  startDate?: string;
  endDate?: string;
}): string => {
  let baseQuery = generateOverviewQuery(options);
  return `${baseQuery}
| summarize Count = count() by CustomerFacingStatus
| sort by Count desc`;
};

/**
 * Generates query for Internal Status Snapshot breakdown
 */
export const generateInternalStatusQuery = (options?: {
  products?: string[];
  dateRange?: "2years" | "all" | "custom";
  startDate?: string;
  endDate?: string;
}): string => {
  let baseQuery = generateOverviewQuery(options);
  return `${baseQuery}
| summarize Count = count() by InternalStatus
| sort by Count desc`;
};

/**
 * Generates query for Source breakdown chart
 */
export const generateSourceBreakdownQuery = (options?: {
  products?: string[];
  dateRange?: "2years" | "all" | "custom";
  startDate?: string;
  endDate?: string;
}): string => {
  let baseQuery = generateOverviewQuery(options);
  return `${baseQuery}
| summarize Count = count() by SubmittingOrganization
| project Source = SubmittingOrganization, Count
| sort by Count desc`;
};

/**
 * Generates query for Geography breakdown chart
 */
export const generateGeographyBreakdownQuery = (options?: {
  products?: string[];
  dateRange?: "2years" | "all" | "custom";
  startDate?: string;
  endDate?: string;
}): string => {
  let baseQuery = generateOverviewQuery(options);
  return `${baseQuery}
| summarize Count = count() by A16Region
| project Region = iif(isempty(A16Region), "Not Available", A16Region), Count
| sort by Count desc`;
};

/**
 * Generates query for Priority breakdown chart
 */
export const generatePriorityBreakdownQuery = (options?: {
  products?: string[];
  dateRange?: "2years" | "all" | "custom";
  startDate?: string;
  endDate?: string;
}): string => {
  let baseQuery = generateOverviewQuery(options);
  return `${baseQuery}
| where Priority != "Not Available" and isnotempty(Priority)
| summarize Count = count() by Priority
| sort by Count desc`;
};

/**
 * Generates query for Monthly Request Counts trend
 */
export const generateMonthlyTrendQuery = (options?: {
  products?: string[];
}): string => {
  let baseQuery = generateOverviewQuery({ ...options, dateRange: "all" });
  return `${baseQuery}
| extend YearMonth = format_datetime(FeatureEvidenceCreatedDate, "yyyy-MM")
| summarize Count = count() by YearMonth
| sort by YearMonth asc`;
};

/**
 * Generates query for average customer request time to disposition by month
 * Mirrors the Monthly Insights "Average Customer Request Time to Disposition by Month" chart.
 */
export const generateMonthlyAvgDispositionQuery = (options?: {
  products?: string[];
}): string => {
  const baseQuery = generateOverviewQuery({ ...options, dateRange: "all" });
  return `${baseQuery}
| where EndToEndDays > 0 and isnotempty(FeatureEvidenceCreatedDate)
| extend YearMonth = format_datetime(FeatureEvidenceCreatedDate, "yyyy-MM")
| summarize AvgDispositionDays = round(avg(todecimal(EndToEndDays)), 1) by YearMonth
| sort by YearMonth asc`;
};

/**
 * Generates query for SLA Performance analysis
 */
export const generateSLAPerformanceQuery = (options?: {
  products?: string[];
  dateRange?: "2years" | "all" | "custom";
  startDate?: string;
  endDate?: string;
}): string => {
  let baseQuery = generateOverviewQuery(options);
  return `${baseQuery}
| where isnotempty(DecisionCustomerFacingStatus) and EndToEndDays > 0
| summarize 
    AvgDays = round(avg(todecimal(EndToEndDays)), 1),
    P90Days = round(percentile(todecimal(EndToEndDays), 90), 1),
    TargetDays = max(todecimal(SLATarget)),
    Count = count()
    by DecisionCustomerFacingStatus
| project Stage = DecisionCustomerFacingStatus, AvgDays, P90Days, TargetDays, Count
| sort by AvgDays desc`;
};

/**
 * Generates query for Customers Waiting Longest
 */
export const generateCustomersWaitingQuery = (options?: {
  products?: string[];
  dateRange?: "2years" | "all" | "custom";
  startDate?: string;
  endDate?: string;
}): string => {
  let baseQuery = generateOverviewQuery(options);
  return `${baseQuery}
| where IsFinalStatus == false and isnotempty(FeatureEvidenceCreatedDate)
| summarize 
    OpenCount = count(),
    MaxWaitDays = max(datetime_diff("day", now(), FeatureEvidenceCreatedDate))
    by TPID, TPAccountName, ProductName
| sort by MaxWaitDays desc
| take 20`;
};

/**
 * Generates filtered query for DCR Automation tab
 */
export const generateDCRAutomationQuery = (options?: {
  offerings?: string[];
  azureServices?: string[];
  categories?: string[];
  failureReasons?: string[];
}): string => {
  let query = CTL_DCR_AUTOMATION_QUERY;
  const filters: string[] = [];

  if (options?.offerings && options.offerings.length > 0) {
    const list = options.offerings.map(o => `"${o}"`).join(", ");
    filters.push(`OfferingName in (${list})`);
  }

  if (options?.azureServices && options.azureServices.length > 0) {
    const list = options.azureServices.map(s => `"${s}"`).join(", ");
    filters.push(`AZServiceName in (${list})`);
  }

  if (options?.categories && options.categories.length > 0) {
    const categoryClauses = options.categories
      .map((category) => {
        switch (category) {
          case "Successfully Created (First Attempt)":
            return `(DCRPresent and DaysToCreateDCR <= 3)`;
          case "Successfully Created (After Retry)":
            return `(DCRPresent and DaysToCreateDCR > 3)`;
          case "DCR Pending Triage":
            return `(not(DCRPresent) and TriageStatus == "CXE Triage Needed")`;
          case "Not Converted to DCR":
            return `(not(DCRPresent) and TriageStatus != "CXE Triage Needed")`;
          default:
            return "";
        }
      })
      .filter(Boolean);

    if (categoryClauses.length > 0) {
      filters.push(`(${categoryClauses.join(" or ")})`);
    }
  }

  if (options?.failureReasons && options.failureReasons.length > 0) {
    const reasonClauses = options.failureReasons
      .map((reason) => {
        switch (reason) {
          case "Unmapped Product":
            return `(not(IsSupportedProduct) and not(DCRPresent))`;
          case "Missing TPID":
            return `(isempty(TPID) and IsSupportedProduct and not(DCRPresent))`;
          case "Invalid UAT State":
            return `(not(HasValidStatus) and IsSupportedProduct and isnotempty(TPID) and not(DCRPresent))`;
          case "Missing Parent Feature":
            return `(not(IsLinkedToValidFeature) and IsSupportedProduct and isnotempty(TPID) and HasValidStatus and not(DCRPresent))`;
          default:
            return "";
        }
      })
      .filter(Boolean);

    if (reasonClauses.length > 0) {
      filters.push(`(${reasonClauses.join(" or ")})`);
    }
  }

  if (filters.length > 0) {
    query += `\n| where ${filters.join(" and ")}`;
  }

  return query;
};

/**
 * Generates filtered query for Prioritize and Decide tab
 */
export const generatePrioritizeDecideQuery = (options?: {
  products?: string[];
  priorities?: string[];
  statuses?: string[];
}): string => {
  let query = CTL_PRIORITIZE_DECIDE_QUERY;
  const filters: string[] = [];

  if (options?.products && options.products.length > 0) {
    const list = options.products.map(p => `"${p}"`).join(", ");
    filters.push(`ProductName in (${list})`);
  }

  if (options?.priorities && options.priorities.length > 0) {
    const list = options.priorities.map(p => `"${p}"`).join(", ");
    filters.push(`Priority in (${list})`);
  }

  if (options?.statuses && options.statuses.length > 0) {
    const list = options.statuses.map(s => `"${s}"`).join(", ");
    filters.push(`InternalStatus in (${list})`);
  }

  if (filters.length > 0) {
    query += `\n| where ${filters.join(" and ")}`;
  }

  return query;
};
